import React, { createContext, useContext } from 'react'

import { useApolloClient } from '@apollo/client'
import type { DefaultContext } from '@apollo/client'
import { sendGTMEvent } from '@next/third-parties/google'
import { v4 as uuidv4 } from 'uuid'

import {
  ClientEvent,
  PublishWebEventDocument,
  PublishWebEventInput,
} from '@graphql/react-apollo'

import { useBranch } from '@web-app/context/BranchContext'
import { useBraze } from '@web-app/context/BrazeContext'
import type { ClientSessionData } from '@web-app/hoc/withSession'
import { ANALYTICS_EVENTS, BRANCH_EVENTS } from '@web-app/lib/analyticsEvents'

export type EventData = Record<string, unknown>

// Mirrors ClientEventInput's optional promoted columns — a closed, schema-owned
// set, not per-feature. Ids passed here become top-level event columns instead
// of data keys; warehouse joins against the mobile apps' rows depend on the
// same promotion. Destinations are not the caller's: every event reaches the
// warehouse and the data layer, Branch gets BRANCH_EVENTS, and `braze` is the
// one opt-in — Braze bills per data point, so only an event a campaign reads
// goes.
export type TrackEventOptions = {
  braze?: boolean
  partnerOfferId?: string
  servedImpressionId?: string
  partnerId?: string
  adId?: string
}

type EventTrackingContextType = {
  trackEvent: (
    eventName: string,
    data?: EventData,
    options?: TrackEventOptions,
  ) => void
  trackImpression: (impressionData: {
    partnerOfferId: string
    servedImpressionId: string
    partnerId: string
    placementId?: string
    data?: EventData
  }) => void
}

export const EventTrackingContext = createContext<
  EventTrackingContextType | undefined
>(undefined)

export const EventTrackingProvider: React.FC<{
  children: React.ReactNode
  sessionData?: ClientSessionData
}> = ({ children, sessionData }) => {
  const apolloClient = useApolloClient()
  const { publishBranchEvent } = useBranch()
  const { publishBrazeEvent } = useBraze()

  // TODO(event-caller-identity): the offers emitters deliberately send no
  // `comp` data key so their payloads match the mobile rows exactly. If
  // per-surface tracing becomes necessary, add caller identity centrally here
  // (a TrackEventOptions field), not back into each feature payload.
  const trackEvent = (
    eventName: string,
    data: EventData = {},
    options: TrackEventOptions = {},
  ) => {
    const {
      braze = false,
      partnerOfferId,
      servedImpressionId,
      partnerId,
      adId,
    } = options
    // One id for the warehouse row and the data-layer push: a pixel sends it
    // as event_id, and the backend's Conversions API copy dedupes on it.
    const requestId = uuidv4()
    const input: PublishWebEventInput = {
      clientMutationId: uuidv4(),
      event: {
        name: eventName,
        data: {
          ...data,
          ...sessionData?.utmParams,
        },
        requestId,
        ...(partnerOfferId && { partnerOfferId: String(partnerOfferId) }),
        ...(servedImpressionId && {
          servedImpressionId: String(servedImpressionId),
        }),
        ...(partnerId && { partnerId: String(partnerId) }),
        ...(adId && { adId: String(adId) }),
      },
    }

    apolloClient
      .mutate({
        mutation: PublishWebEventDocument,
        variables: { input },
        // Analytics never alarm the user; `satisfies` keeps the key typed.
        context: { errorToast: false } satisfies DefaultContext,
      })
      .catch((error: unknown) => {
        console.error('Error publishing web event:', error)
      })

    // Fixed shape: GTM keeps the last value of every key, so an omitted key
    // would carry the previous event's. UTMs stay off; GA4 reads the URL.
    try {
      sendGTMEvent({
        event: eventName,
        request_id: requestId,
        source: data.source ?? null,
        comp: data.comp ?? null,
      })
    } catch (error) {
      console.error('Error sending GTM event:', error)
    }

    if (BRANCH_EVENTS.includes(eventName)) {
      try {
        publishBranchEvent?.(eventName, data)
      } catch (error) {
        console.error('Error publishing Branch event:', error)
      }
    }

    if (braze) {
      try {
        publishBrazeEvent?.(eventName, data)
      } catch (error) {
        console.error('Error publishing Braze event:', error)
      }
    }
  }

  const trackImpression = (impressionData: {
    partnerOfferId: string
    servedImpressionId: string
    partnerId: string
    placementId?: string
    data?: EventData
  }) => {
    const eventObject: ClientEvent = {
      name: ANALYTICS_EVENTS.impression,
      requestId: uuidv4(),
      partnerOfferId: String(impressionData.partnerOfferId),
      servedImpressionId: String(impressionData.servedImpressionId),
      partnerId: String(impressionData.partnerId),
      data: {
        ...(impressionData.data || {}),
        ...sessionData?.utmParams,
      },
    }

    const impressionContext = {
      errorToast: false,
      ...(impressionData.placementId
        ? { headers: { 'Placement-Id': String(impressionData.placementId) } }
        : {}),
    } satisfies DefaultContext

    const input: PublishWebEventInput = {
      clientMutationId: uuidv4(),
      event: eventObject,
    }

    apolloClient
      .mutate({
        mutation: PublishWebEventDocument,
        variables: { input },
        context: impressionContext,
      })
      .catch((error: unknown) => {
        console.error('Error publishing impression event:', error)
      })
  }

  return (
    <EventTrackingContext.Provider value={{ trackEvent, trackImpression }}>
      {children}
    </EventTrackingContext.Provider>
  )
}

export const useEventTracking = () => {
  const context = useContext(EventTrackingContext)
  if (context === undefined) {
    throw new Error(
      'useEventTracking must be used within a EventTrackingProvider',
    )
  }
  return context
}
